import { ArtistWidget } from '@eventbrite/artist-widget';
import { track } from '@eventbrite/datalayer-library';
import React, { useEffect, useRef } from 'react';
import { LISTING_HEAP_IDS } from '../../constants';
import { EventDetailsMainInner } from '../EventDetails/EventDetails';
import { useFetchArtists } from '../hooks/useFetchArtists';
import { useIntersecting } from '../hooks/useIntersecting';
import ArtistSkeleton from './ArtistSkeleton';
import type { ArtistWidget as ArtistData } from './types';
import { getArtistTranslatedTexts, getEventTypeConfig } from './utils';

// Define the type for the props
interface ArtistWidgetWrapperProps {
    eventId: string;
    artistData: ArtistData;
}

export const ArtistWidgetWrapper = (props: ArtistWidgetWrapperProps) => {
    const elementRef = useRef<HTMLDivElement>(null);
    const artistTranslatedTexts = getArtistTranslatedTexts();
    const eventTypeConfig = getEventTypeConfig();

    const { artistType, artistList } = props.artistData;

    // Use the custom hook to fetch artists
    const artists = useFetchArtists(props.eventId, artistList);

    // Use the hook to detect when the element is in view
    const [isIntersecting] = useIntersecting({
        ref: elementRef,
        rootMargin: '0px',
    });

    useEffect(() => {
        if (isIntersecting) {
            track({
                eventName: LISTING_HEAP_IDS.ARTIST_WIDGET_VIEW,
                eventData: { artistType },
            });
        }
    }, [isIntersecting, artistType]);

    // Return null if artists is null (indicating an error)
    if (artists === null) {
        return null;
    }

    const artistData = {
        artistType: eventTypeConfig[artistType].label,
        artistRole: eventTypeConfig[artistType].role,
        artistList: artists,
    };

    return (
        <EventDetailsMainInner>
            <div ref={elementRef}>
                {artists.length === 0 ? (
                    <ArtistSkeleton
                        artistType={artistData.artistType}
                        artistList={artistList}
                    />
                ) : (
                    <ArtistWidget
                        artistData={artistData}
                        translatedTexts={artistTranslatedTexts}
                    />
                )}
            </div>
        </EventDetailsMainInner>
    );
};
